/**
 * simulator/pages/autoescuela/facturacionEjemplo.js
 * Datos de EJEMPLO de la Facturación de autoescuela del simulador de ventas (8/10/2026).
 * Ficha: docs/features/simulador-autoescuela-facturacion-ejemplo.md
 *
 * Función PURA de la fecha (`now`): siempre hay facturas de hoy, del mes y del trimestre en curso y de los dos
 * trimestres anteriores, y gastos (facturas recibidas) en los mismos periodos. Nada sale del navegador.
 *
 *  - Facturas emitidas: matrículas, bonos de 10/20 prácticas, prácticas sueltas, uso del vehículo en el examen y
 *    cursos (CAP, recuperación de puntos). Como en la app real (c_generateAdhocInvoice), el precio lleva el IVA de la
 *    empresa (21 %) INCLUIDO y la base sale del total. Clientes = alumnos de mockData (y ex-alumnos de clientNames).
 *  - Tasas de la DGT: la app real NO las factura (el contrato dice «no incluidas»; son un suplido que la autoescuela
 *    paga en nombre del alumno). Aquí van solo a la CUENTA del alumno (cargo + pago), nunca a una factura ni al IVA.
 *  - Cobros: cada factura de un alumno actual deja en su cuenta el cargo y el pago (efectivo, tarjeta, Bizum o
 *    transferencia). Tres quedan sin cobrar → «Pendiente de cobro» en Pagos, una de ellas un plazo VENCIDO. Sin cobros
 *    por Stripe: los cobros online salen apagados en la demo y el primer cobro online es el que se enseña en vivo.
 *  - Gastos: alquiler del local (IVA 21 % + retención 19 %), seguro de la flota (exento), combustible, taller, ITV
 *    (IVA + tasa exenta), teléfono, luz, gestoría y publicidad. Proveedores ficticios con NIF de control válido.
 */
import { quarterRange, quarterOf } from "../../utils/gastos/gastosRules";

export const IVA_EJEMPLO = 21;
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const INSTRUCTORES = { "inst-001": "Carlos Navarro", "inst-002": "Ana Belén Ruiz", "inst-003": "Miguel Ángel Torres" };

// Alumnos de mockData (SECTOR_DATA.autoescuela.mockAlumnos): el test comprueba que existen con este nombre.
export const ALUMNOS_EJEMPLO = {
  "alu-001": { name: "Alejandro García", inst: "inst-001", dni: "12345678A" },
  "alu-002": { name: "Sofía Martínez", inst: "inst-002", dni: "23456789B" },
  "alu-003": { name: "Hugo Fernández", inst: "inst-001", dni: "34567890C" },
  "alu-004": { name: "Lucía Rodríguez", inst: "inst-003", dni: "45678901D" },
  "alu-005": { name: "Pablo Sánchez", inst: "inst-002", dni: "56789012E" },
  "alu-006": { name: "Marta López", inst: "inst-003", dni: "67890123F" },
  "alu-007": { name: "Raúl Ortega", inst: null, dni: "78901234G" },
};
// Ex-alumnos (ya aprobados) y alumnos de cursos: nombres de clientNames de mockData, sin ficha de alumno.
export const EX_ALUMNOS_EJEMPLO = {
  daniel: { name: "Daniel Hernández", inst: "inst-002" },
  carmen: { name: "Carmen Torres", inst: "inst-001" },
  adrian: { name: "Adrián Ruiz", inst: "inst-003" },
  elena: { name: "Elena Moreno", inst: "inst-002" },
  javier: { name: "Javier Díaz", inst: "inst-003", dni: "48291735S" },
  natalia: { name: "Natalia Jiménez", inst: "inst-001" },
  sergio: { name: "Sergio González", inst: "inst-003", dni: "21573864W" },
  irene: { name: "Irene Castro", inst: "inst-003" },
  alvaro: { name: "Álvaro Romero", inst: "inst-003", dni: "74102389T" },
};

// Precios con IVA incluido (tarifa típica de una autoescuela de ciudad en 2026).
export const CONCEPTOS_EJEMPLO = {
  matricula: { name: "Matrícula permiso B (teórica incluida)", price: 150 },
  matriculaA2: { name: "Matrícula permiso A2 + 8 prácticas (1.er plazo de 2)", price: 210 },
  bono10: { name: "Bono 10 prácticas", price: 330 },
  bono20: { name: "Bono 20 prácticas", price: 620 },
  practica: { name: "Práctica suelta (45 min)", price: 35 },
  examen: { name: "Uso del vehículo en el examen práctico", price: 60 },
  cap: { name: "Curso CAP formación continua (35 h)", price: 290 },
  puntos: { name: "Curso de recuperación parcial de puntos (12 h)", price: 220 },
};

/** Tasa de la DGT (permisos A y B, 2026). Suplido: solo en la cuenta del alumno. */
export const TASA_DGT_CENTS = 9405;

// [díasAtrás, cliente, conceptos[], formato]. Cliente = alumno_id de mockData o clave de EX_ALUMNOS_EJEMPLO.
const FACTURAS = [
  // Hoy: siempre hay algo en «Hoy» (el filtro por defecto de la pantalla, como en la app real).
  [0, "alu-001", ["examen"], "ticket"],
  [0, "alu-006", ["practica", "practica"], "ticket"],
  [0, "alvaro", ["cap"], "a4"],
  // Este mes / trimestre en curso (y lo que caiga en el anterior si el trimestre acaba de empezar).
  [1, "alu-002", ["practica"], "ticket"],
  [2, "alu-007", ["matricula"], "a4"],
  [3, "alu-002", ["practica"], "ticket"],
  [5, "alu-004", ["practica"], "ticket"],
  [7, "alu-006", ["practica"], "ticket"],
  [9, "alu-001", ["practica", "practica"], "ticket"],
  [12, "alu-001", ["practica"], "ticket"],
  [15, "alvaro", ["puntos"], "a4"],
  [20, "alu-006", ["practica"], "ticket"],
  [25, "sergio", ["cap"], "a4"],
  [30, "alu-004", ["matriculaA2"], "a4"],
  [35, "irene", ["examen"], "ticket"],
  [40, "alu-001", ["bono10"], "ticket"],
  [45, "natalia", ["examen"], "ticket"],
  [55, "natalia", ["practica"], "ticket"],
  [60, "alu-003", ["examen"], "ticket"],
  [65, "javier", ["cap"], "a4"],
  [69, "alu-002", ["bono10"], "ticket"],
  [70, "alu-002", ["matricula"], "a4"],
  [70, "irene", ["bono10"], "ticket"],
  [75, "alu-003", ["practica", "practica"], "ticket"],
  [80, "elena", ["examen"], "ticket"],
  [84, "alu-006", ["bono20"], "ticket"],
  [85, "alu-006", ["matricula"], "a4"],
  [90, "alu-003", ["practica"], "ticket"],
  [94, "alu-001", ["bono10"], "ticket"],
  [95, "alu-001", ["matricula"], "a4"],
  [96, "adrian", ["examen"], "ticket"],
  [100, "carmen", ["examen"], "ticket"],
  [108, "irene", ["bono10"], "ticket"],
  [110, "irene", ["matricula"], "a4"],
  [110, "daniel", ["examen"], "ticket"],
  [115, "adrian", ["practica"], "ticket"],
  [118, "alu-005", ["bono10"], "ticket"],
  [120, "alu-005", ["matricula"], "a4"],
  [120, "natalia", ["bono10"], "ticket"],
  [125, "natalia", ["matricula"], "a4"],
  [130, "carmen", ["bono10"], "ticket"],
  [139, "elena", ["bono20"], "ticket"],
  [140, "elena", ["matricula"], "a4"],
  [149, "adrian", ["bono10"], "ticket"],
  [150, "adrian", ["matricula"], "a4"],
  [158, "alu-003", ["bono20"], "ticket"],
  [160, "alu-003", ["matricula"], "a4"],
  [168, "carmen", ["bono10"], "ticket"],
  [170, "carmen", ["matricula"], "a4"],
  [175, "daniel", ["bono20"], "ticket"],
  [178, "daniel", ["matricula"], "a4"],
];

const METODOS = ["tarjeta", "efectivo", "bizum", "transferencia"];

const DAY_MS = 86400000;
const isoDay = (ms) => new Date(ms).toISOString().slice(0, 10);
const utcMidnight = (now) => Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
const fmtEs = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

function clienteDe(key) {
  if (ALUMNOS_EJEMPLO[key]) return { alumno_id: key, ...ALUMNOS_EJEMPLO[key] };
  return { alumno_id: null, ...EX_ALUMNOS_EJEMPLO[key] };
}

// Hora de la factura: hoy, unos minutos/horas antes de `now` (nunca en el futuro ni ayer); otros días, horario de oficina.
function createdAt(now, daysAgo, k) {
  const midnight = utcMidnight(now);
  if (daysAgo === 0) return new Date(Math.max(now.getTime() - (k + 1) * 47 * 60000, midnight + (k + 1) * 60000)).toISOString();
  const minutes = 8 * 60 + ((daysAgo * 137 + k * 53) % (10 * 60)); // 08:00–18:00 UTC
  return new Date(midnight - daysAgo * DAY_MS + minutes * 60000).toISOString();
}

/** Base e IVA de un total con IVA incluido (mismo cálculo que addMockInvoice). */
export function splitIva(total, rate = IVA_EJEMPLO) {
  const base = round2(total / (1 + rate / 100));
  return { base, iva: round2(total - base) };
}

/**
 * Facturas emitidas de ejemplo, de la más reciente a la más antigua, con la numeración AE-AAAA-NNNN correlativa
 * por año. Devuelve también `lastSeqThisYear` (para que la siguiente factura de la demo siga la serie).
 */
export function buildFacturasEjemplo(now = new Date()) {
  const todayCount = {};
  const rows = FACTURAS.map(([daysAgo, key, conceptos, format], i) => {
    todayCount[daysAgo] = (todayCount[daysAgo] || 0) + 1;
    const cli = clienteDe(key);
    const lines = conceptos.map((c) => {
      const { name, price } = CONCEPTOS_EJEMPLO[c];
      return { name, quantity: 1, unit_price: price, discount_pct: 0, total: price };
    });
    const total = round2(lines.reduce((s, l) => s + l.total, 0));
    const { base, iva } = splitIva(total);
    return {
      invoice_id: `ae-ejemplo-${i}`,
      source: "autoescuela",
      format,
      client_name: cli.name,
      client_phone: "",
      client_nif: format === "a4" ? cli.dni || "" : "",
      alumno_id: cli.alumno_id,
      lines,
      total,
      base,
      iva,
      iva_rate: IVA_EJEMPLO,
      worker_name: cli.inst ? INSTRUCTORES[cli.inst] : "",
      created_at: createdAt(now, daysAgo, todayCount[daysAgo] - 1),
      verifactu_status: "accepted",
      ejemplo: true,
    };
  }).sort((a, b) => a.created_at.localeCompare(b.created_at));

  // Numeración por año: el año más antiguo ya venía facturando (empieza en la 24); los siguientes, desde la 1.
  const firstYear = rows.length ? rows[0].created_at.slice(0, 4) : "";
  const seqByYear = {};
  for (const r of rows) {
    const y = r.created_at.slice(0, 4);
    seqByYear[y] = (seqByYear[y] || (y === firstYear ? 23 : 0)) + 1;
    r.number = `AE-${y}-${String(seqByYear[y]).padStart(4, "0")}`;
  }
  const thisYear = String(now.getFullYear());
  return { invoices: rows.reverse(), lastSeqThisYear: seqByYear[thisYear] || 0 };
}

/**
 * Movimientos de la cuenta de los alumnos de mockData (forma de autoescuelaBillingStore.addMovimiento): por cada
 * factura, el cargo y su cobro; tasas de la DGT (suplido); y lo que queda pendiente.
 */
export function buildMovimientosEjemplo(invoices, now = new Date()) {
  const out = [];
  let n = 0;
  const push = (m) => { n += 1; out.push({ mov_id: m.mov_id || `mov-ejemplo-${n}`, created_at: `${m.fecha}T10:00:00.000Z`, ejemplo: true, ...m }); };
  const alumnoInv = invoices.filter((f) => f.alumno_id).slice().reverse(); // de la más antigua a la más reciente
  alumnoInv.forEach((f, i) => {
    const fecha = f.created_at.slice(0, 10);
    const concepto = `${f.lines.map((l) => l.name).join(" + ")} (factura ${f.number})`;
    const importe = Math.round(f.total * 100);
    // El cargo de una factura hecha con «Facturar» lleva su invoice_id, como en la app (la factura automática del final
    // del día no lo vuelve a facturar al cobrarlo).
    push({ alumno_id: f.alumno_id, alumno_name: f.client_name, tipo: "cargo", concepto, importe_cents: importe, fecha, invoice_id: f.invoice_id, invoice_number: f.number });
    // El bono de Sofía se pagó a medias: debe 130 €.
    const pagado = f.alumno_id === "alu-002" && f.lines[0].name === CONCEPTOS_EJEMPLO.bono10.name ? 20000 : importe;
    push({ alumno_id: f.alumno_id, alumno_name: f.client_name, tipo: "pago", concepto: pagado < importe ? `Pago a cuenta del ${concepto}` : concepto, importe_cents: pagado, fecha, metodo: METODOS[i % METODOS.length] });
    // Tasa de la DGT junto a la matrícula: suplido, sin factura ni IVA. La de Raúl (recién matriculado), sin pagar.
    if (/^Matrícula/.test(f.lines[0].name)) {
      const tasa = `Tasa DGT (suplido, sin IVA): permiso ${f.alumno_id === "alu-004" ? "A2" : "B"}`;
      push({ alumno_id: f.alumno_id, alumno_name: f.client_name, tipo: "cargo", concepto: tasa, importe_cents: TASA_DGT_CENTS, fecha });
      if (f.alumno_id !== "alu-007") push({ alumno_id: f.alumno_id, alumno_name: f.client_name, tipo: "pago", concepto: tasa, importe_cents: TASA_DGT_CENTS, fecha, metodo: "bizum" });
    }
  });
  // Lucía: 2.º plazo de la matrícula VENCIDO hace 12 días (cargo del plan de pagos, se factura al cobrarlo) y la
  // cancelación tardía de pr-h04 (mov-late-001 en mockData).
  const mat = invoices.find((f) => f.alumno_id === "alu-004" && f.lines[0].name === CONCEPTOS_EJEMPLO.matriculaA2.name);
  if (mat) {
    const vence = isoDay(Date.parse(`${mat.created_at.slice(0, 10)}T12:00:00Z`) + 18 * DAY_MS);
    push({ alumno_id: "alu-004", alumno_name: mat.client_name, tipo: "cargo", concepto: `Matrícula permiso A2 + 8 prácticas (2.º plazo de 2, vencido el ${fmtEs(vence)})`, importe_cents: 21000, fecha: vence });
    const late = isoDay(Date.parse(`${mat.created_at.slice(0, 10)}T12:00:00Z`) + 26 * DAY_MS);
    push({ mov_id: "mov-late-001", alumno_id: "alu-004", alumno_name: mat.client_name, tipo: "cargo", concepto: "Cancelación tardía de la práctica (menos de 24 h)", importe_cents: 2500, fecha: late, source: "late_cancellation" });
  }
  // Cobrado HOY sin factura (15/10/2026): lo que la tarjeta «Facturas automáticas al final del día» enseña en la vista
  // previa. Pagado entero: no cambia ninguna deuda de la demo.
  const hoy = new Date(now).toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });
  const a = (h) => new Date(Date.parse(`${hoy}T12:00:00Z`) - (12 - h) * 3600000).toISOString();
  push({ alumno_id: "alu-001", alumno_name: "Alejandro García", tipo: "cargo", concepto: "Práctica suelta", importe_cents: 3500, fecha: hoy, created_at: a(7) });
  push({ alumno_id: "alu-001", alumno_name: "Alejandro García", tipo: "pago", concepto: "Práctica suelta", importe_cents: 3500, fecha: hoy, metodo: "efectivo", created_at: a(8) });
  push({ alumno_id: "alu-006", alumno_name: "Marta López", tipo: "cargo", concepto: "Pack 5 prácticas", importe_cents: 17500, fecha: hoy, created_at: a(7) });
  push({ alumno_id: "alu-006", alumno_name: "Marta López", tipo: "pago", concepto: "Pack 5 prácticas", importe_cents: 17500, fecha: hoy, metodo: "tarjeta", created_at: a(8) });
  return out.sort((a, b) => b.fecha.localeCompare(a.fecha));
}

// ── Gastos (facturas recibidas) ─────────────────────────────────────────────
// Proveedores ficticios (ninguno es una empresa real); NIF con el dígito de control correcto.
const PROV = {
  alquiler: ["Inmuebles Plaza Mayor SL", "B30618425", "alquiler", "transferencia"],
  seguro: ["Seguros Mutua del Volante SA", "A28471639", "servicios", "domiciliacion"],
  gasolinaA: ["Gasolinera Los Olmos SL", "B03975216", "combustible", "tarjeta"],
  gasolinaB: ["Estación de Servicio Ronda Sur SL", "B03629185", "combustible", "tarjeta"],
  taller: ["Talleres Hermanos Gil SL", "B03784154", "servicios", "transferencia"],
  itv: ["ITV Vega Baja SA", "A03157922", "servicios", "tarjeta"],
  telefono: ["Telecomunicaciones Norte SL", "B54926373", "suministros", "domiciliacion"],
  luz: ["Comercializadora Eléctrica Brisa SA", "A48052161", "suministros", "domiciliacion"],
  gestoria: ["Asesoría Robles y Asociados SL", "B03291846", "servicios", "domiciliacion"],
  publicidad: ["Publicidad Digital Levante SL", "B03715638", "servicios", "transferencia"],
};

// [proveedor, día del mes, [[descripción, tipo IVA, base]], IRPF %, origen, prefijo nº]
const MENSUALES = [
  ["alquiler", 1, [["Alquiler del local de la autoescuela", 21, 850]], 19, "manual", "ALQ"],
  ["gasolinaA", 3, [["Gasóleo A (flota)", 21, 74.38]], 0, "ia", "T"],
  ["telefono", 4, [["Fibra + 3 líneas móviles", 21, 54.5]], 0, "ia", "TN"],
  ["luz", 6, [["Electricidad del local", 21, 96.2]], 0, "ia", "EB"],
  ["gestoria", 10, [["Asesoría fiscal, laboral y contable", 21, 120]], 0, "ia_corregida", "AR"],
  ["gasolinaB", 11, [["Gasolina 95 (moto A2)", 21, 23.14], ["Gasóleo A (flota)", 21, 61.98]], 0, "ia", "RS"],
  ["publicidad", 15, [["Campaña de anuncios en redes sociales", 21, 150]], 0, "ia", "PDL"],
  ["gasolinaA", 18, [["Gasóleo A (flota)", 21, 82.64]], 0, "ia", "T"],
  ["gasolinaB", 25, [["Gasóleo A (flota)", 21, 69.42]], 0, "ia", "RS"],
];

function buildGasto(id, provKey, date, items, irpf, origen, number, estado = "pagada") {
  const [supplier, nif, category, pay] = PROV[provKey];
  const byRate = {};
  for (const [, rate, base] of items) byRate[rate] = round2((byRate[rate] || 0) + base);
  const tax_lines = Object.keys(byRate).map(Number).sort((a, b) => b - a)
    .map((rate) => ({ rate, base: byRate[rate], tax: round2((byRate[rate] * rate) / 100), re_rate: null, re: null }));
  const base_total = round2(tax_lines.reduce((a, t) => a + t.base, 0));
  const tax_total = round2(tax_lines.reduce((a, t) => a + t.tax, 0));
  const withholding_amount = irpf ? round2((base_total * irpf) / 100) : 0;
  return {
    gasto_id: id,
    doc_type: "factura",
    supplier_name: supplier,
    supplier_nif: nif,
    number,
    date,
    tax_lines,
    base_total,
    tax_total,
    re_total: 0,
    withholding_rate: irpf || null,
    withholding_amount,
    total: round2(base_total + tax_total - withholding_amount),
    payment_method: pay,
    estado,
    category,
    origen,
    has_doc: origen !== "manual",
    lines: items.map(([description, rate, base]) => ({ description, quantity: 1, unit_price: base, amount: base, iva_rate: rate })),
    ejemplo: true,
  };
}

/** Facturas recibidas de ejemplo de los últimos 6 meses (mes en curso hasta hoy), de la más reciente a la más antigua. */
export function buildGastosEjemplo(now = new Date()) {
  const today = isoDay(utcMidnight(now));
  const y0 = Number(today.slice(0, 4));
  const m0 = Number(today.slice(5, 7)) - 1;
  const d0 = Number(today.slice(8, 10));
  const out = [];
  for (let k = 0; k < 6; k += 1) {
    const y = y0 + Math.floor((m0 - k) / 12);
    const m = (((m0 - k) % 12) + 12) % 12;
    const ym = `${y}-${String(m + 1).padStart(2, "0")}`;
    const dateOf = (day) => `${ym}-${String(day).padStart(2, "0")}`;
    const ok = (day) => k > 0 || day <= d0; // nada en el futuro
    MENSUALES.forEach(([prov, day, items, irpf, origen, pre], j) => {
      if (!ok(day)) return;
      // La publicidad del mes en curso aún no se ha pagado (filtro «Pendiente»).
      const estado = k === 0 && prov === "publicidad" ? "pendiente" : "pagada";
      out.push(buildGasto(`ejemplo-${ym}-${j}`, prov, dateOf(day), items, irpf, origen, `${pre}-${ym.replace("-", "")}-${String(j + 11).padStart(3, "0")}`, estado));
    });
    // Seguro de la flota: prima trimestral el día 1 del trimestre. Exento de IVA (art. 20.1.16.º LIVA).
    if (m % 3 === 0) {
      out.push(buildGasto(`ejemplo-${ym}-seguro`, "seguro", dateOf(1), [["Prima trimestral flota (3 turismos + 1 moto)", 0, 1140]], 0, "ia", `POL-${ym.replace("-", "")}-7781`));
    }
    // Taller cada dos meses e ITV una vez.
    if (k % 2 === 1 && ok(18)) {
      out.push(buildGasto(`ejemplo-${ym}-taller`, "taller", dateOf(18), [["Revisión y cambio de aceite y filtros", 21, 186.4], ["Pastillas de freno delanteras", 21, 59.2]], 0, "ia", `HG-${ym.replace("-", "")}-204`));
    }
    if (k === 2 && ok(12)) {
      out.push(buildGasto(`ejemplo-${ym}-itv`, "itv", dateOf(12), [["Inspección técnica periódica (turismo)", 21, 38.9], ["Tasa de Tráfico de la ITV", 0, 4.36]], 0, "manual", `ITV-${ym.replace("-", "")}-5530`));
    }
  }
  return out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

/**
 * Lado «Ingresos» del Excel conjunto de un trimestre: las facturas emitidas en él (misma forma que mockIngresos de
 * citas: { label, count, base, iva, total }).
 */
export function ingresosDeTrimestre(invoices, range) {
  const list = (invoices || []).filter((f) => {
    const d = String(f.created_at || "").slice(0, 10);
    return d >= range.from && d <= range.to;
  });
  let base = 0;
  let iva = 0;
  let total = 0;
  for (const f of list) {
    const t = Number(f.total) || 0;
    const b = Number.isFinite(Number(f.base)) ? Number(f.base) : splitIva(t).base;
    base += b;
    iva += Number.isFinite(Number(f.iva)) ? Number(f.iva) : round2(t - b);
    total += t;
  }
  return { label: range.label, count: list.length, base: round2(base), iva: round2(iva), total: round2(total) };
}

/** Rango del trimestre de una fecha ISO. */
export const trimestreDe = (iso) => quarterRange(Number(iso.slice(0, 4)), quarterOf(iso));

/** Todo junto: lo que siembra autoescuelaBillingStore al cargar. */
export function buildFacturacionEjemplo(now = new Date()) {
  const { invoices, lastSeqThisYear } = buildFacturasEjemplo(now);
  return { invoices, lastSeqThisYear, movimientos: buildMovimientosEjemplo(invoices, now), gastos: buildGastosEjemplo(now) };
}
